const DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 2;
const CACHE_LIMIT = 30;
const ABORT_ERROR_NAME = 'AbortError';
/* Prefixed: every authored script shares one global scope, so a bare HISTORY_LIMIT here
   would collide with the same name in another kl-*.js and kill whichever loads second. */
const KL_SEARCH_HISTORY_STORAGE_KEY = 'kl-search-history';
const KL_SEARCH_HISTORY_HARD_LIMIT = 10;

/* Dawn's, from assets/base.css — not ours to rename into data-kl-* compliance. Its cart
   drawer and modals hold the page with the same class. */
const KL_SEARCH_SCROLL_LOCK_CLASS = 'overflow-hidden';

/*
  Sold-out products are left out by the API rather than by the panel, which is why this is a
  request parameter and not a filter over what comes back. Asking for eight and dropping the
  sold-out ones afterwards would show five; asking the API to leave them out returns eight
  products that can be bought.

  Two of the API's three values are reachable here. 'hide' drops them, 'last' keeps them
  after everything in stock, and the section setting chooses between the two. 'show' would
  scatter them among the in-stock results and no setting asks for that.
*/
function readUnavailableProductsOption(datasetValue) {
  return datasetValue === 'last' ? 'last' : 'hide';
}

function buildSuggestUrl({ suggestUrl, sectionId, query, resourceLimit, unavailableProducts }) {
  const parameters = new URLSearchParams({
    q: query,
    'resources[type]': 'product,collection,query',
    'resources[limit]': String(resourceLimit),

    /*
      Without this the limit is spent across every type at once, not applied to each of
      them: asking for ten returned three products, three collections and four suggestions —
      ten things in total — and asking for one returned a single suggestion and no products
      at all. `each` is what makes the number mean "ten products AND ten collections AND ten
      suggestions", which is the panel the reference storefront shows.
    */
    'resources[limit_scope]': 'each',
    'resources[options][unavailable_products]': unavailableProducts,
    section_id: sectionId,
  });

  return `${suggestUrl}?${parameters.toString()}`;
}

function normalizeQuery(value) {
  return value.trim().replace(/\s+/g, ' ');
}

function readResultsMarkup(responseText) {
  const responseDocument = new DOMParser().parseFromString(responseText, 'text/html');
  const resultsElement = responseDocument.querySelector('[data-kl-search-results]');

  if (!resultsElement) return null;

  return resultsElement.outerHTML;
}

/* Pure: takes the stored list and the term, returns the list that should replace it.
   Newest first, and a repeat of something already in the list moves it to the front
   rather than appearing twice, so searching the same word twice does not fill the
   panel with it. Matched case-insensitively but stored as typed. */
function addTermToHistory(storedTerms, term) {
  const withoutTerm = storedTerms.filter(
    (storedTerm) => storedTerm.toLowerCase() !== term.toLowerCase(),
  );

  return [term, ...withoutTerm].slice(0, KL_SEARCH_HISTORY_HARD_LIMIT);
}

/* Storage is the one thing here that can throw on us: Safari's private mode and a
   browser set to block site data both make localStorage unreadable. History is a
   convenience, so a failure returns nothing and the section stays hidden. */
function readSearchHistory() {
  try {
    const storedValue = localStorage.getItem(KL_SEARCH_HISTORY_STORAGE_KEY);

    if (!storedValue) return [];

    const storedTerms = JSON.parse(storedValue);

    if (!Array.isArray(storedTerms)) return [];

    return storedTerms.filter((storedTerm) => typeof storedTerm === 'string' && storedTerm.length > 0);
  } catch (error) {
    console.debug('kl-search: could not read the search history', { error });
    return [];
  }
}

function writeSearchHistory(terms) {
  try {
    localStorage.setItem(KL_SEARCH_HISTORY_STORAGE_KEY, JSON.stringify(terms));
  } catch (error) {
    console.debug('kl-search: could not save the search history', { terms, error });
  }
}

/*
  Each typed word in quotes, which is what the results page is asked for, so a product has
  to carry every one of them rather than any one of them.

  Unquoted, Shopify's storefront search matches a single word across every indexed field
  including the description, so "mahjong" returned ~1000 of 2411 products where only 12
  carry the word, and "mahjong necklace" returned more than "mahjong". options[fields] is
  ignored for product results and AND is read as a term rather than an operator, so quoting
  is the only lever. snippets/kl-search-query.liquid does the same thing in Liquid.

  Word by word rather than the whole query as one quoted phrase, because a phrase is matched
  in the order it was typed: "necklace mahjong", "good luck bracelet" and "womens necklace"
  all found nothing against a catalogue that answers all three.

  Deliberately not applied to the predictive request below: that one is the top ten by
  relevance, which is already specific, and quoting returns no query suggestions at all —
  the panel's Suggestions column would empty out.
*/
function buildSearchQuery(term) {
  if (term.includes('"')) return term;

  return term
    .split(' ')
    .map((searchWord) => `"${searchWord}"`)
    .join(' ');
}

/*
  unavailable_products matches the hidden field both search forms carry. Leaving it off
  does not fall back to nothing — it falls back to the Search & Discovery setting, which a
  merchandiser can change. Measured on the live catalogue: hide is 41 pages of "necklace"
  and show is 47, and the store's default is hide today. Today is the word that matters.
*/
function buildSearchUrl(searchUrl, term) {
  const query = encodeURIComponent(buildSearchQuery(term));

  return `${searchUrl}?q=${query}&options[prefix]=last&options[unavailable_products]=hide`;
}

/*
  Built from the form rather than from a string of our own, so every hidden field the form
  declares is carried. Hand-written, the URL silently dropped unavailable_products the
  moment this stopped being a native submit, and nothing on the page said so.
*/
function buildFormSubmitUrl(formElement, term) {
  const parameters = new URLSearchParams(new FormData(formElement));

  parameters.set('q', buildSearchQuery(term));

  return `${formElement.getAttribute('action')}?${parameters}`;
}

function rememberResults(cache, query, markup) {
  if (cache.size >= CACHE_LIMIT) {
    const oldestQuery = cache.keys().next().value;

    cache.delete(oldestQuery);
  }

  cache.set(query, markup);
}

function initSearch(dialogElement) {
  const formElement = dialogElement.querySelector('[data-kl-search-form]');
  const inputElement = dialogElement.querySelector('[data-kl-search-input]');
  const clearElement = dialogElement.querySelector('[data-kl-search-clear]');
  const clearLabelElement = dialogElement.querySelector('[data-kl-search-clear-label]');
  const defaultElement = dialogElement.querySelector('[data-kl-search-default]');
  const resultsSlotElement = dialogElement.querySelector('[data-kl-search-results-slot]');

  if (!inputElement || !clearElement || !defaultElement || !resultsSlotElement) return null;

  const {
    klSearchSectionId: sectionId,
    klSearchSuggestUrl: suggestUrl,
    klSearchUrl: searchUrl,
  } = dialogElement.dataset;
  const resourceLimit = Number.parseInt(dialogElement.dataset.klSearchLimit, 10) || 10;
  const unavailableProducts = readUnavailableProductsOption(
    dialogElement.dataset.klSearchUnavailableProducts,
  );
  const clearLabelText = dialogElement.dataset.klSearchClearLabel || '';
  const closeLabelText = dialogElement.dataset.klSearchCloseLabel || '';

  const isPredictiveEnabled =
    dialogElement.dataset.klSearchPredictive !== undefined && Boolean(suggestUrl) && Boolean(sectionId);

  const resultsCache = new Map();

  let debounceTimerId = null;
  let abortController = null;
  let renderedQuery = '';

  function showDefaultState() {
    renderedQuery = '';
    resultsSlotElement.hidden = true;
    resultsSlotElement.replaceChildren();
    defaultElement.hidden = false;
    inputElement.setAttribute('aria-expanded', 'false');
  }

  /* Called on every render, not once at start-up: the results are replaced wholesale by
     the fragment the section returns, so the empty list Liquid ships comes back each
     time and has to be filled again. */
  function renderHistory() {
    const historyElement = resultsSlotElement.querySelector('[data-kl-search-history]');

    if (!historyElement || !searchUrl) return;

    const listElement = historyElement.querySelector('[data-kl-search-history-list]');

    if (!listElement) return;

    const shownLimit = Number.parseInt(historyElement.dataset.klSearchHistoryLimit, 10) || 5;
    const terms = readSearchHistory().slice(0, shownLimit);

    listElement.replaceChildren();

    if (terms.length === 0) {
      historyElement.hidden = true;
      return;
    }

    terms.forEach((term) => {
      const itemElement = document.createElement('li');
      const linkElement = document.createElement('a');

      itemElement.className = 'kl-search__menu-item';
      linkElement.className = 'kl-search__menu-link';
      linkElement.href = buildSearchUrl(searchUrl, term);
      linkElement.textContent = term;
      linkElement.setAttribute('data-kl-search-link', '');

      itemElement.append(linkElement);
      listElement.append(itemElement);
    });

    historyElement.hidden = false;
  }

  /* The typed words, not the thing that was clicked: the list is meant to let a visitor
     pick up a search they abandoned, so "red" is the useful entry even when what they
     opened from it was one product. */
  function saveCurrentQuery() {
    const term = normalizeQuery(inputElement.value);

    if (term.length < MIN_QUERY_LENGTH) return;

    writeSearchHistory(addTermToHistory(readSearchHistory(), term));
  }

  function showResultsState(markup) {
    resultsSlotElement.innerHTML = markup;
    resultsSlotElement.hidden = false;
    defaultElement.hidden = true;
    inputElement.setAttribute('aria-expanded', 'true');
    renderHistory();
  }

  function syncClearLabel(query) {
    if (!clearLabelElement) return;

    clearLabelElement.textContent = query.length > 0 ? clearLabelText : closeLabelText;
  }

  function cancelPendingRequest() {
    if (debounceTimerId !== null) {
      clearTimeout(debounceTimerId);
      debounceTimerId = null;
    }

    if (abortController) {
      abortController.abort();
      abortController = null;
    }

    dialogElement.classList.remove('is-loading');
  }

  async function requestResults(query) {
    const controller = new AbortController();

    abortController = controller;
    dialogElement.classList.add('is-loading');

    try {
      const response = await fetch(
        buildSuggestUrl({ suggestUrl, sectionId, query, resourceLimit, unavailableProducts }),
        { signal: controller.signal },
      );

      if (!response.ok) throw new Error(`Predictive search responded ${response.status}`);

      const markup = readResultsMarkup(await response.text());

      if (!markup) throw new Error('Predictive search returned no results fragment');

      rememberResults(resultsCache, query, markup);

      if (normalizeQuery(inputElement.value) !== query) return;

      renderedQuery = query;
      showResultsState(markup);
    } catch (error) {
      if (error?.name === ABORT_ERROR_NAME) return;

      console.debug('kl-search: could not load results', { query, error });

      if (abortController === controller) showDefaultState();
    } finally {
      if (abortController === controller) {
        abortController = null;
        dialogElement.classList.remove('is-loading');
      }
    }
  }

  function handleQueryChange() {
    const query = normalizeQuery(inputElement.value);

    syncClearLabel(inputElement.value);
    cancelPendingRequest();

    if (!isPredictiveEnabled || query.length < MIN_QUERY_LENGTH) {
      showDefaultState();
      return;
    }

    if (query === renderedQuery) return;

    const cachedMarkup = resultsCache.get(query);

    if (cachedMarkup) {
      renderedQuery = query;
      showResultsState(cachedMarkup);
      return;
    }

    debounceTimerId = setTimeout(() => {
      debounceTimerId = null;
      requestResults(query);
    }, DEBOUNCE_MS);
  }

  /*
    Hold the page still while the panel is over it.

    showModal() makes everything behind the panel inert, but it does not stop the page
    scrolling — so a wheel over the panel scrolled the document underneath, which is what the
    scrollbar on the right was doing. The panel scrolls inside itself instead; it is
    overflow-y: auto with a max-height of the viewport.

    overflow-hidden is Dawn's class, declared in assets/base.css and used by its cart drawer
    and both of its modals. Joining that convention rather than writing a body rule of our
    own is what keeps this one line: the class already exists, and every overlay in the theme
    releases the page the same way.

    hasLockedPageScroll is why it is not simply add-on-open and remove-on-close. The cart
    drawer can be open behind this panel — assets/kl-search.js parents it inside — and it
    holds the same class for its own reasons. Removing a lock this panel did not take would
    hand the page back while the drawer still wants it still. Same guard
    cart-disclosure-modal.js uses.
  */
  let hasLockedPageScroll = false;

  function lockPageScroll() {
    if (document.body.classList.contains(KL_SEARCH_SCROLL_LOCK_CLASS)) return;

    document.body.classList.add(KL_SEARCH_SCROLL_LOCK_CLASS);
    hasLockedPageScroll = true;
  }

  function unlockPageScroll() {
    if (!hasLockedPageScroll) return;

    document.body.classList.remove(KL_SEARCH_SCROLL_LOCK_CLASS);
    hasLockedPageScroll = false;
  }

  function openSearch() {
    if (dialogElement.open) return;

    try {
      dialogElement.showModal();
    } catch (error) {
      console.debug('kl-search: could not open the panel', { error });
      return;
    }

    lockPageScroll();
    inputElement.focus({ preventScroll: true });
  }

  function closeSearch() {
    cancelPendingRequest();

    if (dialogElement.open) dialogElement.close();
  }

  /*
    Released on `close` rather than inside closeSearch, because the dialog closes by routes
    this file never sees — Escape is the browser's own, not ours — and every one of them
    fires this event.
  */
  dialogElement.addEventListener('close', unlockPageScroll);

  function moveFocusThroughResults(step) {
    const linkElements = [...dialogElement.querySelectorAll('[data-kl-search-link]')];

    if (linkElements.length === 0) return;

    const currentIndex = linkElements.indexOf(document.activeElement);

    if (currentIndex === -1) {
      linkElements[step > 0 ? 0 : linkElements.length - 1].focus();
      return;
    }

    const nextIndex = currentIndex + step;

    if (nextIndex < 0) {
      inputElement.focus();
      return;
    }

    if (nextIndex >= linkElements.length) return;

    linkElements[nextIndex].focus();
  }

  inputElement.addEventListener('input', handleQueryChange);

  inputElement.addEventListener('keydown', (event) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    event.preventDefault();
    moveFocusThroughResults(event.key === 'ArrowDown' ? 1 : -1);
  });

  dialogElement.addEventListener('keydown', (event) => {
    if (event.target === inputElement) return;
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

    event.preventDefault();
    moveFocusThroughResults(event.key === 'ArrowDown' ? 1 : -1);
  });

  clearElement.addEventListener('click', () => {
    if (inputElement.value.length === 0) {
      closeSearch();
      return;
    }

    inputElement.value = '';
    handleQueryChange();
    inputElement.focus();
  });

  dialogElement.addEventListener('click', (event) => {
    if (event.target !== dialogElement) return;

    closeSearch();
  });

  /* Delegated, because the results are replaced on every keystroke — a listener bound to
     the links themselves would be thrown away with the markup that carried it. Acting on
     a result is what makes a query worth remembering; typing one that was abandoned is
     not, which is why nothing is saved as it is typed. */
  resultsSlotElement.addEventListener('click', (event) => {
    if (!event.target.closest('[data-kl-search-link]')) return;

    saveCurrentQuery();
  });

  dialogElement.addEventListener('cancel', cancelPendingRequest);

  dialogElement.addEventListener('close', () => {
    cancelPendingRequest();
    inputElement.value = '';
    syncClearLabel('');
    showDefaultState();
  });

  if (formElement) {
    formElement.addEventListener('submit', (event) => {
      const term = normalizeQuery(inputElement.value);

      event.preventDefault();

      if (term.length === 0) {
        inputElement.focus();
        return;
      }

      saveCurrentQuery();

      /*
        The quotes go on the URL rather than into the field. Writing them into the input
        and letting the form submit natively did show them, for however long the browser
        took to leave the page — which on the search panel is long enough to read.

        Navigating by hand is what that costs. Without JavaScript the form submits itself
        and asks for the bare words, which is the behaviour this panel had before.
      */
      location.assign(buildFormSubmitUrl(formElement, term));
    });
  }

  syncClearLabel('');

  return { openSearch, closeSearch };
}

const searchInstances = new Map();

function registerSearch(dialogElement) {
  const instance = initSearch(dialogElement);

  if (instance) searchInstances.set(dialogElement, instance);
}

function findSearchInstance(rootElement) {
  const dialogElement = rootElement.querySelector('[data-kl-search]');

  if (!dialogElement) return null;

  return searchInstances.get(dialogElement) || null;
}

document.addEventListener('click', (event) => {
  const triggerElement = event.target.closest('[data-kl-search-open]');

  if (!triggerElement) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

  const instance = findSearchInstance(document);

  if (!instance) return;

  event.preventDefault();
  instance.openSearch();
});

document.querySelectorAll('[data-kl-search]').forEach(registerSearch);

document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-kl-search]').forEach(registerSearch);
});

document.addEventListener('shopify:section:select', (event) => {
  findSearchInstance(event.target)?.openSearch();
});

document.addEventListener('shopify:section:deselect', (event) => {
  findSearchInstance(event.target)?.closeSearch();
});

/*
  Let the cart drawer sit over the panel instead of behind it.

  Adding to cart from Popular products opens the drawer underneath the panel, and no z-index
  can fix it: the panel is shown with showModal(), which puts it in the browser's top layer,
  and the top layer paints above every stacking context on the page. Dawn's cart drawer is an
  ordinary element at z-index 100 — there is no number it could carry that would beat a modal
  dialog. The reference storefront has no such problem because its panel is not a dialog.

  So the drawer is moved inside the panel for as long as both are open. Within the panel it
  is an ordinary descendant again, its own z-index applies, and it draws over the panel's
  content — while the panel stays open behind it, which is what the reference does.

  Moving a custom element is safe here, and only because of how Dawn wrote this one: every
  listener is attached in CartDrawer's constructor and there is no connectedCallback, so
  re-parenting re-runs nothing and loses nothing. Check that again before moving any other
  custom element this way.

  It is put back whenever either one closes, so nothing is left parented to a hidden dialog.

  One thing the panel must never grow: a transform, or a transform animation, or will-change
  on one. Any of those makes it a containing block for fixed descendants, and the drawer is a
  fixed box sized by inset: 0 — it would be confined to the panel's height rather than the
  viewport's. The open animation was split in two for exactly this reason; the slide lives on
  .kl-search__panel inside, and assets/kl-search.css says so where it happens.

  Watched rather than wired to the add button, because "the drawer opened" is the thing that
  matters and it can happen for reasons this file knows nothing about. The observer has no
  end condition — the drawer can open any number of times — so it is never disconnected.
*/
function watchCartDrawerForSearch() {
  const cartDrawer = document.querySelector('cart-drawer');

  if (!cartDrawer) return;

  const homeParent = cartDrawer.parentElement;
  const homeNextSibling = cartDrawer.nextSibling;

  if (!homeParent) return;

  function returnCartDrawerHome() {
    if (cartDrawer.parentElement === homeParent) return;

    homeParent.insertBefore(cartDrawer, homeNextSibling);
  }

  function syncCartDrawerPlacement() {
    const openPanel = [...document.querySelectorAll('[data-kl-search]')].find(
      (dialogElement) => dialogElement.open
    );

    if (cartDrawer.classList.contains('active') && openPanel) {
      if (cartDrawer.parentElement !== openPanel) openPanel.appendChild(cartDrawer);
      return;
    }

    returnCartDrawerHome();
  }

  try {
    new MutationObserver(syncCartDrawerPlacement).observe(cartDrawer, {
      attributes: true,
      attributeFilter: ['class'],
    });

    document.querySelectorAll('[data-kl-search]').forEach((dialogElement) => {
      dialogElement.addEventListener('close', returnCartDrawerHome);
    });
  } catch (error) {
    console.debug('kl-search: could not follow the cart drawer', { error });
  }
}

watchCartDrawerForSearch();
